import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../lib/track';

/**
 * BookingContext — one place to control the WriteUpp booking modal.
 *
 * Enhancements (Feb 2026):
 *  - `openBooking(intent)` now optionally takes a small intent object that
 *    lets callers deep-link to a specific treatment category or
 *    practitioner. The modal renders a header chip so the visitor sees
 *    what they're about to book (e.g. "TMJ Assessment with Krina") and
 *    the intent is passed into the tracking event so analytics can
 *    attribute conversions per service page.
 *  - The URL `?book=1&category=tmj` auto-opens the modal on page load,
 *    so ad campaigns and email CTAs can drop the visitor straight into
 *    the right funnel.
 *
 * Currently WriteUpp's public booking widget does not accept a query-
 * string service filter, so "deep link" means the modal header prompts
 * the visitor to pick the right option once the iframe loads. If/when
 * WriteUpp exposes a per-service URL we swap the iframe src accordingly
 * from the CATEGORY_META map below without touching call sites.
 */

// Historical booking data across 989 appointments shows a clean split:
//   • Krina handles all "Initial Jaw Assessment", Follow-ups, NESA and Dry Needling.
//   • Mercy handles all TMJ Velvet and Jaw Massage sessions.
// We use this to auto-attribute the category → practitioner label shown
// in the modal header. It's a hint, not a hard binding — the WriteUpp
// booking page still lets the visitor pick anyone in the calendar.
export const CATEGORY_META = {
  tmj: {
    label: 'TMJ Assessment',
    practitioner: 'Krina Panchal',
    // How we render the header chip; keeps things human.
    headline: 'TMJ jaw assessment with Krina',
  },
  botox: {
    label: 'Jaw Tension Consultation',
    practitioner: 'Dr Kartik Patel',
    headline: 'Jaw tension assessment & treatment with Dr Kartik Patel',
  },
  massage: {
    label: 'Jaw / Back Massage',
    practitioner: 'Mercy Phillips',
    headline: 'Jaw & back massage with Mercy',
  },
  velvet: {
    label: 'TMJ Velvet',
    practitioner: 'Mercy Phillips',
    headline: 'TMJ Velvet treatment with Mercy',
  },
  nesa: {
    label: 'NESA Neuromodulation',
    practitioner: 'Krina Panchal',
    headline: 'NESA neuromodulation with Krina',
  },
  virtual: {
    label: 'Virtual Consultation',
    practitioner: 'Krina Panchal',
    headline: 'Virtual consultation with Krina',
  },
  followup: {
    label: 'Follow-up',
    practitioner: 'Krina Panchal',
    headline: 'Follow-up with Krina',
  },
};

const BookingContext = createContext({
  isOpen: false,
  intent: null,
  openBooking: () => {},
  closeBooking: () => {},
});

/**
 * Wrap the app in <BookingProvider> so any component can open the WriteUpp booking modal
 * by calling useBookingModal().openBooking(intent).
 */
export const BookingProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [intent, setIntent] = useState(null);
  const location = useLocation();

  const openBooking = useCallback((intentArg = null) => {
    // Normalise: strings are treated as category shortcuts.
    const normalised = typeof intentArg === 'string' ? { category: intentArg } : (intentArg || null);
    // Attach metadata from the category so the modal doesn't need to look it up.
    if (normalised?.category && CATEGORY_META[normalised.category]) {
      Object.assign(normalised, { meta: CATEGORY_META[normalised.category] });
    }
    setIntent(normalised);
    trackEvent(
      'booking_modal_open',
      normalised?.category
        ? `WriteUpp booking · category=${normalised.category}${normalised.source ? ` · from=${normalised.source}` : ''}`
        : 'WriteUpp booking iframe'
    );
    setIsOpen(true);
  }, []);

  const closeBooking = useCallback(() => setIsOpen(false), []);

  // URL-driven auto-open: `?book=1&category=tmj` on any page opens the
  // modal pre-scoped. The query is consumed on first render only so a
  // back-navigation doesn't re-open it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(location.search);
    if (params.get('book') === '1') {
      const category = params.get('category') || null;
      openBooking(category ? { category, source: 'url' } : null);
      // Strip the params so the URL is shareable without re-opening.
      params.delete('book');
      params.delete('category');
      const clean = params.toString();
      window.history.replaceState(
        {},
        '',
        `${location.pathname}${clean ? `?${clean}` : ''}${location.hash}`,
      );
    }
    // We only care about pathname changes, not query — the query gets
    // consumed above. Guard by pathname so mid-page filter changes don't
    // fire this again.
  }, [location.pathname]);

  const value = useMemo(
    () => ({ isOpen, intent, openBooking, closeBooking }),
    [isOpen, intent, openBooking, closeBooking]
  );

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBookingModal = () => useContext(BookingContext);
